import { useEffect, useRef, type ReactNode } from 'react';
import { ErrorApi } from './api';

export function Cabecera({
  titulo,
  alVolver,
  accion,
}: {
  titulo: string;
  alVolver?: () => void;
  accion?: ReactNode;
}) {
  return (
    <header className="cabecera">
      {alVolver ? (
        <button type="button" className="boton icono" onClick={alVolver} aria-label="Volver">
          ←
        </button>
      ) : (
        /* El logo solo en la pantalla raíz: en las de dentro el sitio lo ocupa
           el botón de volver, que es lo que hace falta ahí. Y `alt` vacío
           porque al lado va el título — un lector de pantalla que dijese
           "FactorLibre" antes de cada título sería ruido. */
        <img
          className="cabecera-marca"
          src="/marca/factorlibre-claro.png"
          alt=""
          width={87}
          height={15}
        />
      )}
      <h1>{titulo}</h1>
      {accion}
    </header>
  );
}

/**
 * Confirmación para lo que no tiene vuelta atrás.
 *
 * Se usa `<dialog>` del navegador y no un div flotante porque trae gratis lo
 * que cuesta hacer bien a mano: el foco atrapado dentro, cerrar con Escape, y
 * que un lector de pantalla anuncie que se ha abierto algo modal.
 *
 * El botón que confirma NO es el que queda bajo el pulgar por defecto: en una
 * pantalla de borrado, el gesto cómodo tiene que ser el de cancelar.
 */
export function Confirmacion({
  titulo,
  detalle,
  textoConfirmar,
  peligrosa = false,
  ocupado = false,
  alConfirmar,
  alCancelar,
}: {
  titulo: string;
  detalle?: ReactNode;
  textoConfirmar: string;
  peligrosa?: boolean;
  ocupado?: boolean;
  alConfirmar: () => void;
  alCancelar: () => void;
}) {
  const dialogo = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    // showModal() no es idempotente: llamarlo sobre un diálogo ya abierto lanza.
    if (!dialogo.current?.open) dialogo.current?.showModal();
  }, []);

  return (
    <dialog
      ref={dialogo}
      className="dialogo"
      // Escape también cierra: sin esto el diálogo desaparecería del DOM del
      // navegador pero React seguiría creyéndolo abierto.
      onCancel={(evento) => {
        evento.preventDefault();
        if (!ocupado) alCancelar();
      }}
    >
      <h2>{titulo}</h2>
      {detalle && <div className="dialogo-detalle">{detalle}</div>}
      <div className="dialogo-acciones">
        <button
          type="button"
          className="boton secundario ancho"
          onClick={alCancelar}
          disabled={ocupado}
        >
          Cancelar
        </button>
        <button
          type="button"
          className={`boton ancho${peligrosa ? ' peligro' : ''}`}
          onClick={alConfirmar}
          disabled={ocupado}
        >
          {ocupado ? 'Un momento…' : textoConfirmar}
        </button>
      </div>
    </dialog>
  );
}

/**
 * Muestra un error de forma útil.
 *
 * El texto lo pone quien mejor lo sabe: las validaciones y las reglas de Gextia
 * ya vienen redactadas para el usuario, así que se enseñan tal cual. Solo se
 * sustituye el mensaje cuando el original no ayudaría — un fallo de red, por
 * ejemplo, donde lo importante es que no se ha perdido nada.
 */
export function AvisoError({ error }: { error: unknown }) {
  if (!error) return null;

  if (error instanceof ErrorApi) {
    if (error.codigo === 'SIN_RED' || error.codigo === 'GEXTIA_NO_DISPONIBLE') {
      return (
        <p className="aviso atencion">
          Sin conexión con Gextia ahora mismo. Vuelve a intentarlo en un momento.
        </p>
      );
    }
    return <p className="aviso error">{error.message}</p>;
  }

  // Un error que no viene del backend es un fallo nuestro. El mensaje amable no
  // vale de nada si nadie puede saber qué pasó: por eso el detalle técnico se
  // deja a mano, plegado. Son diez personas en la empresa, no un producto de
  // consumo — que puedan copiar el error y mandarlo ahorra media hora a todos.
  const detalle = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  console.error('[gastos] error no controlado', error);

  return (
    <div className="aviso error" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
      <span>Algo ha fallado por nuestra parte.</span>
      <details style={{ marginTop: '0.4rem' }}>
        <summary style={{ cursor: 'pointer', fontSize: '0.8rem' }}>Detalle técnico</summary>
        <code style={{ display: 'block', marginTop: '0.3rem', fontSize: '0.75rem' }}>
          {detalle}
        </code>
      </details>
    </div>
  );
}

export function Cargando({ texto = 'Cargando…' }: { texto?: string }) {
  return <p className="cargando">{texto}</p>;
}

export function Vacio({ titulo, sugerencia }: { titulo: string; sugerencia?: string }) {
  return (
    <div className="vacio">
      <p>{titulo}</p>
      {sugerencia && <p className="tarjeta-meta">{sugerencia}</p>}
    </div>
  );
}
